import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';

import { EmptyState, Meter, Panel, Spinner, Stat, relativeTime } from '../components/common/index.jsx';
import { work } from '../lib/api.js';

/** The annotator's work list. */
export function QueuePage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('');
  const [onlyUnfinished, setOnlyUnfinished] = useState(true);

  useEffect(() => {
    work.queue().then((r) => setItems(r.items)).catch(() => setItems([]));
    work.summary().then(setSummary).catch(() => {});
  }, []);

  // A real, working search box - the legacy dashboard had the state but never rendered
  // the input, so the filter was dead code.
  const visible = useMemo(() => {
    if (!items) return [];
    const needle = filter.trim().toLowerCase();
    return items.filter((item) => {
      if (onlyUnfinished && (item.status === 'submitted' || item.status === 'approved')) return false;
      if (!needle) return true;
      return (
        String(item.image_id).toLowerCase().includes(needle) ||
        String(item.title || '').toLowerCase().includes(needle) ||
        String(item.dataset_key).toLowerCase().includes(needle)
      );
    });
  }, [items, filter, onlyUnfinished]);

  if (items === null) return <Spinner label="Loading your queue…" />;

  const next = visible[0];

  return (
    <div className="page">
      <div className="page-head row-between">
        <div>
          <h1>Your queue</h1>
          <p>Each image has three captions. Annotate all three, then mark the image done.</p>
        </div>
        {next && (
          <button type="button" className="btn primary lg" onClick={() => navigate(`/annotate/${next.task_id}`)}>
            Continue <ArrowRight size={16} />
          </button>
        )}
      </div>

      {summary && (
        <div className="stat-grid section">
          <Stat label="Assigned" value={summary.total.images} sub="images" />
          <Stat label="Done" value={summary.total.done} sub={`${summary.total.samples_done} captions`} />
          <Stat label="Remaining" value={summary.total.remaining} sub="images" />
          <Stat
            label="COCO / Web"
            value={`${summary.by_family?.coco?.images ?? 0} / ${summary.by_family?.web?.images ?? 0}`}
            sub="images assigned"
          />
        </div>
      )}

      <div className="section">
        <Panel
          title="Images"
          actions={
            <div className="row gap-1">
              <label className="row gap-1 small muted" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={onlyUnfinished}
                  onChange={(e) => setOnlyUnfinished(e.target.checked)}
                />
                Unfinished only
              </label>
              <span className="row gap-1">
                <Search size={14} className="faint" />
                <input
                  className="input sm"
                  placeholder="Filter by image id…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label="Filter images"
                />
              </span>
            </div>
          }
          bodyClass="tight"
        >
          {visible.length === 0 ? (
            <EmptyState title={items.length ? 'Nothing matches' : 'No images assigned yet'}>
              {items.length
                ? 'Try clearing the filter.'
                : 'An administrator will assign you a batch of images.'}
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Set</th>
                    <th>Progress</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.assignment_id}>
                      <td className="num faint">{item.position}</td>
                      <td>
                        <Link to={`/annotate/${item.task_id}`}>{item.image_id}</Link>
                        {item.title && <div className="tiny faint">{item.title}</div>}
                      </td>
                      <td>
                        <span className="chip">{item.family === 'coco' ? 'COCO' : 'Web'}</span>
                      </td>
                      <td style={{ minWidth: 120 }}>
                        <Meter value={item.samples_submitted} total={item.samples_total} className="sm" />
                        <span className="tiny faint">
                          {item.samples_submitted}/{item.samples_total} captions
                        </span>
                      </td>
                      <td>
                        <StatusChip status={item.status} />
                      </td>
                      <td className="tiny faint nowrap">{relativeTime(item.updated_at)}</td>
                      <td className="right">
                        <Link className="btn sm" to={`/annotate/${item.task_id}`}>
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

export function StatusChip({ status }) {
  const map = {
    assigned: { cls: '', label: 'not started' },
    in_progress: { cls: 'info', label: 'in progress' },
    submitted: { cls: 'ok', label: 'done' },
    approved: { cls: 'ok', label: 'approved' },
    changes_requested: { cls: 'warn', label: 'changes requested' },
    revoked: { cls: 'danger', label: 'revoked' },
  };
  const info = map[status] || { cls: '', label: status };
  return <span className={`chip ${info.cls}`}>{info.label}</span>;
}
